import { useMemo } from 'react';
import { useTheme } from '@material-ui/core/styles';
import { getProjectClockInAreas } from 'utils/getProjectClockInAreas';

const usePolygons = ({ projects, drawAreaMode, drawnAreas, activeAreaId }) => {
  const theme = useTheme();
  // 🟢 Polygons for the user-drawn areas
  const drawAreaPolygons = useMemo(() => {
    if (!drawAreaMode || !drawnAreas || drawnAreas.length === 0) return [];

    return drawnAreas.map((area) => {
      const isActive = area.id === activeAreaId;
      return {
        // Map.js captures the google polygon instance by the constant
        // 'editable-polygon' id, so the same instance always renders
        // whichever area is active
        id: isActive ? 'editable-polygon' : `drawn-area-${area.id}`,
        areaId: area.id,
        desc: 'Piirretty alue',
        options: {
          strokeColor: theme.palette.primary.main,
          strokeOpacity: isActive ? 0.9 : 0.5,
          strokeWeight: isActive ? 3 : 2,
          fillColor: theme.palette.primary.main,
          fillOpacity: isActive ? 0.3 : 0.15,
          clickable: !isActive,
          draggable: isActive,
          editable: isActive,
          visible: true,
          zIndex: isActive ? 2 : 1,
        },
        paths: area.positions.map((pos) => ({
          lat: Number(pos.latitude),
          lng: Number(pos.longitude),
        })),
      };
    });
  }, [drawAreaMode, drawnAreas, activeAreaId, theme.palette.primary.main]);

  // 🔵 Polygons for project-defined proximity areas
  const projectPolygons = useMemo(() => {
    if (!projects) return [];

    return projects
      .filter(
        (p) =>
          p.proximityToClockInRequired === true &&
          p.latitude &&
          p.longitude &&
          p.proximityToClockInType === 'area'
      )
      .flatMap((p) =>
        getProjectClockInAreas(p).map((area) => ({
          id: `project-area-${p.id}-${area.id}`,
          desc: `${p.projectKey} - ${p.name}`,
          options: {
            strokeColor: theme.palette.primary.main,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: theme.palette.primary.main,
            fillOpacity: 0.4,
            clickable: false,
            draggable: false,
            editable: false,
            visible: true,
            zIndex: 1,
          },
          paths: area.positions.map((pos) => ({
            lat: pos.latitude,
            lng: pos.longitude,
          })),
        }))
      );
  }, [projects, theme.palette.primary.main]);

  // ⚪ Combine both types
  const allPolygons = useMemo(() => {
    const combined = [...projectPolygons, ...drawAreaPolygons];
    return combined;
  }, [projectPolygons, drawAreaPolygons]);

  return { drawAreaPolygons, projectPolygons, allPolygons };
};

export default usePolygons;
